import React, { useState, useEffect } from 'react';
import graphql from 'babel-plugin-relay/macro';
import {
  TextField,
  Select,
  MenuItem,
  Stack,
  Button,
  Autocomplete,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLazyLoadQuery } from 'react-relay';
import {useTranslation} from "react-i18next";

const TAGS_QUERY = graphql`
  query ActionBarTagsQuery {
    tags {
      id
      name
    }
  }
`;

const ActionBar = ({ onSearch, onFilter, onSort, onTagFilter }) => {
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);

  const isMobile = useMediaQuery('(max-width:600px)');
  const data = useLazyLoadQuery(TAGS_QUERY, {});

  useEffect(() => {
    if (data?.tags) {
      setTagOptions(data.tags.map(tag => tag.name));
    }
  }, [data]);

  const handleSearch = () => {
    onSearch(searchText);
    onFilter(filter);
    onSort(sort);
    onTagFilter(selectedTags.slice(0, 3));
  };

  const handleReset = () => {
    setSearchText('');
    setFilter('');
    setSort('');
    setSelectedTags([]);
    onSearch('');
    onFilter('');
    onSort('');
    onTagFilter([]);
  };
  const { t } = useTranslation();

  // Custom sort function for tags: UA > EN > NUM > other
  const sortTags = (tags) => {
    const uaRegex = /^[А-Яа-яІіЇїЄєҐґ]/;
    const enRegex = /^[A-Za-z]/;
    const numRegex = /^[0-9]/;

    const rank = (str) => {
      if (uaRegex.test(str)) return 0;
      if (enRegex.test(str)) return 1;
      if (numRegex.test(str)) return 2;
      return 3;
    };

    return [...tags].sort((a, b) => {
      const rankA = rank(a);
      const rankB = rank(b);
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b);
    });
  };

  const formContent = (
    <Stack spacing={2}>
      <TextField
        label={t('search', 'Пошук за назвою, автором')}
        variant="outlined"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
      />

      <Select
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        displayEmpty
        fullWidth
      >
        <MenuItem value="">{t('filter', 'Фільтр')}</MenuItem>
        <MenuItem value="available">{t('available', 'Доступні')}</MenuItem>
        <MenuItem value="unavailable">{t('unavailable', 'Недоступні')}</MenuItem>
      </Select>

      <Select
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        displayEmpty
        fullWidth
      >
        <MenuItem value="">{t('sort', 'Сортування')}</MenuItem>
        <MenuItem value="title">{t('title', 'Назва')}</MenuItem>
        <MenuItem value="author">{t('author', 'Автор')}</MenuItem>
        <MenuItem value="publisher">{t('publisher', 'Видавець')}</MenuItem>
      </Select>

      <Autocomplete
        multiple
        options={tagOptions}
        getOptionLabel={(option) => option}
        value={selectedTags}
        onChange={(event, newValue) => {
          if (newValue.length <= 3) {
            setSelectedTags(sortTags(newValue));
          }
        }}
        renderInput={(params) => (
          <TextField {...params} autoComplete="off" label={t('tags', 'Теги (макс. 3)')} variant="outlined" />
        )}
        fullWidth
      />

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSearch} fullWidth>
          {t('submit', 'Застосувати')}
        </Button>
        <Button variant="outlined" onClick={handleReset} fullWidth>
          {t('clear', 'Скинути')}
        </Button>
      </Stack>
    </Stack>
  );

  return isMobile ? (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>{t('actionBar', 'Панель дій')}</Typography>
      </AccordionSummary>
      <AccordionDetails>{formContent}</AccordionDetails>
    </Accordion>
  ) : (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      flexWrap="wrap"
      sx={{ mb: 2 }}
    >
      <Box minWidth={180} flex="1 1 180px">
        <TextField
          label={t('search', 'Пошук')}
          variant="outlined"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          fullWidth
        />
      </Box>
      <Box minWidth={130}>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          displayEmpty
          size="small"
          fullWidth
        >
          <MenuItem value="">{t('filter', 'Фільтр')}</MenuItem>
          <MenuItem value="available">{t('available', 'Доступні')}</MenuItem>
          <MenuItem value="unavailable">{t('unavailable', 'Недоступні')}</MenuItem>
        </Select>
      </Box>
      {/* <Box minWidth={150}>
        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          displayEmpty
          size="small"
          fullWidth
        >
          <MenuItem value="">{t('sort', 'Сортування')}</MenuItem>
          <MenuItem value="title">{t('title', 'Назва')}</MenuItem>
          <MenuItem value="author">{t('author', 'Автор')}</MenuItem>
          <MenuItem value="publisher">{t('publisher', 'Видавець')}</MenuItem>
        </Select>
      </Box> */}
      <Box minWidth={200} flex="2 1 200px">
        <Autocomplete
          multiple
          options={tagOptions}
          getOptionLabel={(option) => option}
          value={selectedTags}
          onChange={(event, newValue) => {
            if (newValue.length <= 3) {
              setSelectedTags(sortTags(newValue));
            }
          }}
          renderInput={(params) => (
            <TextField {...params} autoComplete="off" label={t('tags', 'Теги (макс. 3)')} variant="outlined" size="small" />
          )}
          size="small"
          sx={{ minWidth: 200 }}
        />
      </Box>
      <Box display="flex" gap={1}>
        <Button variant="contained" onClick={handleSearch} sx={{color: 'common.white'}} size="small">
          {t('submit', 'Пошук')}
        </Button>
        <Button variant="outlined" onClick={handleReset} size="small">
          {t('clear', 'Скинути')}
        </Button>
      </Box>
    </Box>
  );
};

export default ActionBar;
